import { get, post } from "@/services/axiosUitils";

const ENDPOINT = "/food_scanner/favorite-meal/";

export interface FavoriteMeal {
  favorite_meal_id: string;
  meal_data_id?: string;
  meal_name: string;
  meal_type?: string;
  image_url?: string;
  food_names: string[];
}

const timezoneHeader = () => ({
  "X-User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
});

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function text(row: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

export function parseFavoriteMeal(raw: unknown): FavoriteMeal | null {
  const row = asRecord(raw);
  if (!row) return null;
  const nested = asRecord(row.favorite) ?? asRecord(row.meal) ?? asRecord(row.data);
  const source = nested ?? row;
  const favoriteId = text(source, ["favorite_meal_id", "favourite_meal_id", "id"]);
  if (!favoriteId) return null;
  const foodsRaw = source.food_names ?? source.foods ?? source.food_items;
  const food_names = Array.isArray(foodsRaw)
    ? foodsRaw
        .map((item) => {
          if (typeof item === "string") return item.trim();
          const food = asRecord(item);
          return food ? text(food, ["name", "food_name", "meal_name", "product_name"]) : "";
        })
        .filter(Boolean)
    : [];
  return {
    favorite_meal_id: favoriteId,
    meal_data_id: text(source, ["meal_data_id", "source_meal_id"]) || undefined,
    meal_name: text(source, ["meal_name", "meal_title", "title", "name"]) || food_names[0] || "Meal",
    meal_type: text(source, ["meal_type"]) || undefined,
    image_url: text(source, ["image_url", "image", "meal_image", "document"]) || undefined,
    food_names,
  };
}

function listFromPayload(payload: unknown): FavoriteMeal[] {
  if (Array.isArray(payload)) {
    return payload.map(parseFavoriteMeal).filter((item): item is FavoriteMeal => item != null);
  }
  const root = asRecord(payload);
  if (!root) return [];
  for (const key of ["favorites", "favourite_meals", "results", "data", "meals", "favorite_meals"]) {
    const value = root[key];
    if (Array.isArray(value)) {
      return value.map(parseFavoriteMeal).filter((item): item is FavoriteMeal => item != null);
    }
    const nested = listFromPayload(value);
    if (nested.length > 0) return nested;
  }
  const one = parseFavoriteMeal(root);
  return one ? [one] : [];
}

class FavoriteMealService {
  async list(): Promise<FavoriteMeal[]> {
    const response = await get<unknown>(ENDPOINT, {
      showNotification: false,
      headers: timezoneHeader(),
    });
    return listFromPayload(response);
  }

  async markFavorite(mealDataId: string): Promise<FavoriteMeal | null> {
    const response = await post<unknown>(
      ENDPOINT,
      { is_fav: true, meal_data_id: mealDataId },
      { showNotification: false, headers: timezoneHeader() },
    );
    return (
      parseFavoriteMeal(response) ??
      listFromPayload(response)[0] ?? {
        favorite_meal_id: mealDataId,
        meal_data_id: mealDataId,
        meal_name: "Meal",
        food_names: [],
      }
    );
  }

  async unfavorite(favoriteMealId: string): Promise<void> {
    await post(
      ENDPOINT,
      { is_fav: false, favorite_meal_id: favoriteMealId },
      { showNotification: false, headers: timezoneHeader() },
    );
  }

  async logFromFavorite(input: {
    favorite_meal_id: string;
    date: string;
    meal_type?: string;
  }): Promise<void> {
    await post(
      ENDPOINT,
      {
        is_consider: true,
        favorite_meal_id: input.favorite_meal_id,
        date: input.date,
        ...(input.meal_type ? { meal_type: input.meal_type } : {}),
      },
      { showNotification: false, headers: timezoneHeader() },
    );
  }
}

export default new FavoriteMealService();
